import React, { useEffect, useState } from 'react';

// Live "someone just donated" toast for the public campaign page.
//
// Purely presentational + self-dismissing. The PublicCampaign renderer polls
// the public endpoint, diffs the recent-donors list, and feeds each new donor
// in as a DonationToastItem; the stack renders them pinned to the chosen
// corner and removes each one after it slides out. Gated by the per-campaign
// `showDonationToasts` setting upstream — this component assumes it's wanted.

export type ToastCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface DonationToastItem {
  /** Stable, unique per donation — used as React key and de-dupe id. */
  key: string;
  name: string;
  amount: number;
  /** Currency symbol already resolved by the caller (₪ / $ / …). */
  symbol: string;
  /** Empty when the donation wasn't attributed to an ambassador. */
  ambassadorName?: string;
}

const VISIBLE_MS = 6000;   // how long the card stays fully shown
const EXIT_MS = 400;       // slide-out animation duration

// On phones the desktop card (min 300px wide, 24px amount, fat padding) eats
// most of the screen, so we shrink the footprint and fonts below this width.
const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 480px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const ToastCard: React.FC<{
  item: DonationToastItem;
  primary: string;
  fromLeft: boolean;
  labels: { title: string; donated: (name: string) => string; via: (amb: string) => string };
  onDone: (key: string) => void;
}> = ({ item, primary, fromLeft, labels, onDone }) => {
  const [shown, setShown] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Next frame → trigger the slide-in transition.
    const enter = window.setTimeout(() => setShown(true), 20);
    const hide = window.setTimeout(() => setShown(false), VISIBLE_MS);
    const remove = window.setTimeout(() => onDone(item.key), VISIBLE_MS + EXIT_MS);
    return () => { window.clearTimeout(enter); window.clearTimeout(hide); window.clearTimeout(remove); };
    // item.key is stable for the card's lifetime; onDone is stable from caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.key]);

  // Slide in/out from whichever horizontal edge the stack is pinned to.
  const hidden = fromLeft ? 'translateX(-120%)' : 'translateX(120%)';

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={() => { setShown(false); window.setTimeout(() => onDone(item.key), EXIT_MS); }}
      style={{
        pointerEvents: 'auto',
        cursor: 'pointer',
        minWidth: isMobile ? 0 : 300,
        maxWidth: isMobile ? '100%' : 400,
        width: isMobile ? '100%' : undefined,
        boxSizing: 'border-box',
        background: '#fff',
        borderRadius: isMobile ? 14 : 18,
        boxShadow: '0 14px 40px rgba(0,0,0,0.18), 0 3px 10px rgba(0,0,0,0.08)',
        border: '1px solid #f1f1f4',
        borderInlineStart: `${isMobile ? 5 : 6}px solid ${primary}`,
        padding: isMobile ? '12px 14px' : '18px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 4 : 6,
        fontFamily: "'Heebo', sans-serif",
        transform: shown ? 'translateX(0)' : hidden,
        opacity: shown ? 1 : 0,
        transition: `transform ${EXIT_MS}ms cubic-bezier(0.22,1,0.36,1), opacity ${EXIT_MS}ms ease`,
      }}
    >
      <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 800, color: primary }}>{labels.title}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: isMobile ? 8 : 12 }}>
        <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {labels.donated(item.name)}
        </span>
        <span style={{ fontSize: isMobile ? 19 : 24, fontWeight: 800, color: primary, whiteSpace: 'nowrap' }}>
          {item.symbol}{(item.amount || 0).toLocaleString()}
        </span>
      </div>
      {item.ambassadorName && (
        <div style={{ fontSize: isMobile ? 12 : 14, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span aria-hidden>🤝</span>
          <span>{labels.via(item.ambassadorName)}</span>
        </div>
      )}
    </div>
  );
};

export const DonationToastStack: React.FC<{
  items: DonationToastItem[];
  primary: string;
  /** Which screen corner to pin the stack to. Defaults to bottom-left. */
  corner?: ToastCorner;
  labels: { title: string; donated: (name: string) => string; via: (amb: string) => string };
  onDismiss: (key: string) => void;
}> = ({ items, primary, corner = 'bottom-left', labels, onDismiss }) => {
  const isMobile = useIsMobile();
  if (!items.length) return null;
  const isTop = corner.startsWith('top');
  const isLeft = corner.endsWith('left');
  return (
    <div
      // Pinned to the chosen corner regardless of page direction. dir=ltr keeps
      // the stack layout from flipping under the RTL page. The newest toast
      // sits nearest the corner; older ones stack away from it. On phones we
      // stretch edge-to-edge (small margins) so the full-width cards fit.
      dir="ltr"
      style={{
        position: 'fixed',
        // On desktop the public campaign has the sticky Amutech top bar
        // (~64px tall) pinned at top:0, so a top-anchored stack would render
        // its first card behind it. Start below the bar there. The bar is
        // hidden under 640px (mobile uses a footer bar), so the small inset
        // is fine on phones.
        ...(isTop ? { top: isMobile ? 12 : 80 } : { bottom: isMobile ? 12 : 20 }),
        ...(isMobile
          ? { left: 10, right: 10 }
          : isLeft ? { left: 20 } : { right: 20 }),
        zIndex: 9600,
        display: 'flex',
        flexDirection: isTop ? 'column-reverse' : 'column',
        gap: isMobile ? 8 : 12,
        pointerEvents: 'none',
        maxWidth: isMobile ? undefined : '92vw',
      }}
    >
      {items.map((it) => (
        <ToastCard key={it.key} item={it} primary={primary} fromLeft={isLeft} labels={labels} onDone={onDismiss} />
      ))}
    </div>
  );
};
